import "./index.css";

import { BoltIcon, BrushIcon, LayersIcon, SparkIcon, SparkleIcon } from "@/components/icons";
import { WaitlistForm } from "@/components/WaitlistForm";
import { Button } from "@/components/ui/button";

import afterEmbroidery from "@/assets/after-embroidery.svg";
import beforeSketch from "@/assets/before-sketch.svg";
import logo from "@/stitch-logo-transparent.png";

const steps = [
  {
    id: 1,
    title: "Clean Up",
    description: "We tidy your source artwork, removing noise, smudges, or stray marks before digitizing.",
    icon: BrushIcon,
  },
  {
    id: 2,
    title: "Enhance",
    description: "AI enhancements automatically smooth lines and balance contrast for stitch-ready detail.",
    icon: LayersIcon,
  },
  {
    id: 3,
    title: "Digitize",
    description: "Receive a downloadable embroidery-ready file with stitch sequencing optimized by pros.",
    icon: SparkIcon,
  },
];

const features = [
  {
    title: "AI-Powered Precision",
    description:
      "Upload your design, we handle the clean vectors and stitch paths.",
    icon: SparkleIcon,
  },
  {
    title: "Supports Multiple Formats",
    description: "DST, PES, EXP, JEF, VP3, XXX - export the formats your machines expect, no extra conversions required.",
    icon: LayersIcon,
  },
  {
    title: "Instant Previews",
    description:
      "See thread colors, stitch density, and fill areas in seconds before you ever press 'start' on your machine.",
    icon: BoltIcon,
  },
];

export function App() {
  return (
    <div className="relative overflow-hidden bg-background text-foreground">
      <div className="absolute -top-24 left-1/2 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-primary/20 blur-3xl" />

      <div className="relative mx-auto flex min-h-screen w-full min-w-0 max-w-6xl flex-col px-4 pb-16 pt-10 sm:px-8 lg:px-12">
        <header className="flex min-w-0 flex-col gap-6">
          <nav className="flex min-w-0 items-center justify-between gap-4 rounded-full bg-white/80 px-4 py-4 shadow-sm ring-1 ring-slate-200 backdrop-blur sm:px-6">
            <a href="#" className="flex min-w-0 items-center gap-3 text-lg font-semibold text-slate-900">
              <span className="flex size-10 items-center justify-center rounded-full bg-primary/15 text-primary">
                <img src={logo} alt="StitchFinch logo" className="size-10" />
              </span>
              StitchFinch
            </a>
          </nav>
        </header>

        <main className="flex flex-col gap-24 pt-16">
          <section className="text-center">
            <div className="mx-auto inline-flex max-w-full flex-wrap items-center justify-center gap-2 rounded-full bg-white/80 px-4 py-2 text-sm font-medium text-slate-600 ring-1 ring-slate-200 backdrop-blur">
              <span className="inline-flex size-5 items-center justify-center rounded-full bg-primary/20 text-primary">
                <SparkIcon className="size-3.5" />
              </span>
              AI embroidery digitizing in seconds.
            </div>
            <h1 className="mt-8 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Your Design, Embroidered.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-600">
              StitchFinch instantly transforms your designs into beautiful, machine-ready embroidery files. Just upload and go stitch.
            </p>
            <div className="mx-auto mt-10 w-full min-w-0 max-w-xl rounded-2xl bg-white/80 p-4 text-left shadow-lg ring-1 ring-slate-200 backdrop-blur sm:p-6">
              <WaitlistForm />
            </div>
          </section>

          <section id="how-it-works" className="flex flex-col gap-10">
            <div className="text-center">
              <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-primary">Process</h2>
              <p className="mt-2 text-3xl font-semibold text-slate-900 sm:text-4xl">
                From Design to Embroidery in Three Simple Steps.
              </p>
            </div>
            <div className="grid gap-6 sm:grid-cols-3">
              {steps.map(step => (
                <article
                  key={step.id}
                  className="flex flex-col gap-4 rounded-3xl bg-white/90 p-6 text-left shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-1 hover:shadow-md"
                >
                  <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <step.icon className="size-6" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-400">0{step.id}</p>
                    <h3 className="text-xl font-semibold text-slate-900">{step.title}</h3>
                    <p className="text-sm leading-6 text-slate-600">{step.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section id="pricing" className="flex flex-col gap-10">
            <div className="text-center">
              <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-primary">Why StitchFinch</h2>
              <p className="mt-2 text-3xl font-semibold text-slate-900 sm:text-4xl">Digitizing, Reimagined.</p>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {features.map(feature => (
                <article
                  key={feature.title}
                  className="flex flex-col gap-4 rounded-3xl bg-white/90 p-6 text-left shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-1 hover:shadow-md"
                >
                  <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <feature.icon className="size-6" />
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-xl font-semibold text-slate-900">{feature.title}</h3>
                    <p className="text-sm leading-6 text-slate-600">{feature.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="mx-auto w-full max-w-4xl rounded-[32px] bg-gradient-to-br from-white to-slate-50 p-[1px] shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
            <div className="min-w-0 rounded-[32px] bg-white px-4 py-10 text-center sm:px-10 sm:py-12 md:px-16">
              <h2 className="text-3xl font-semibold text-slate-900 sm:text-4xl">
                Ready to Bring Your Design to Life?
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-base text-slate-600">
                Go from a simple design to a beautiful embroidery design in seconds. No software installs, no dizzying
                settings—just seamless embroidery.
              </p>
              <div className="mt-8 flex justify-center">
                <Button size="lg" className="h-auto min-h-14 max-w-full whitespace-normal px-4 text-base font-semibold sm:px-8" asChild>
                  <a href="#waitlist">Join the waitlist</a>
                </Button>
              </div>
            </div>
          </section>
        </main>

        <footer className="mt-24 flex flex-col gap-6 border-t border-slate-200 pt-8 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} StitchFinch. All rights reserved.</p>
          <div className="flex min-w-0 flex-wrap gap-x-6 gap-y-2">
            <a href="#" className="transition-colors hover:text-slate-700">
              About
            </a>
            <a href="#" className="transition-colors hover:text-slate-700">
              Contact
            </a>
            <a href="/privacy" className="transition-colors hover:text-slate-700">
              Privacy
            </a>
            <a href="#" className="transition-colors hover:text-slate-700">
              Terms of Service
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
